import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import ScheduleIcon from '@mui/icons-material/Schedule';
import { EmaQuestionnaireNotificationScheduling } from './EmaQuestionnaireNotificationScheduling';
import { EmaQuestionnaire } from '@aphp-ema/common/questionnaire';
import { useStudyFormContext } from '../contexts';

type QuestionnaireSchedulingFormProps = {
  questionnaires: EmaQuestionnaire[];
};

export const QuestionnaireSchedulingForm: React.FC<
  QuestionnaireSchedulingFormProps
> = ({ questionnaires }) => {
  const { t } = useTranslation();

  const { saveAndNavigate } = useStudyFormContext();

  const handleEditScheduling = (questionnaireId: string) => {
    saveAndNavigate(`/questionnaire-scheduling/${questionnaireId}/edit`);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        backgroundColor: 'background.paper',
      }}
    >
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <AccessTimeIcon
            sx={{
              color: 'primary.main',
              fontSize: 24,
            }}
          />
          <Typography variant="h6" fontWeight="600" color="text.primary">
            {t('pages.studies.sections.questionnaireScheduling')}
          </Typography>
        </Stack>
      </Box>

      {/* Content Section */}
      {questionnaires.length > 0 ? (
        <Stack spacing={2}>
          {questionnaires.map((questionnaire, index) => (
            <Card
              key={questionnaire.id}
              variant="outlined"
              sx={{
                borderRadius: 2,
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  boxShadow: 2,
                  borderColor: 'primary.main',
                },
              }}
            >
              <CardContent sx={{ pb: 1 }}>
                {/* Questionnaire Header */}
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  sx={{ mb: 2 }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mb: 1 }}
                    >
                      <Chip
                        label={`#${index + 1}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{
                          fontWeight: 600,
                          minWidth: 40,
                        }}
                      />
                      <Typography
                        variant="subtitle1"
                        fontWeight="600"
                        color="text.primary"
                        sx={{
                          wordBreak: 'break-word',
                          lineHeight: 1.3,
                        }}
                      >
                        {questionnaire.title}
                      </Typography>
                    </Stack>
                  </Box>

                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditScheduling(questionnaire.id)}
                    sx={{
                      ml: 2,
                      flexShrink: 0,
                      borderRadius: 1.5,
                      textTransform: 'none',
                      fontWeight: 500,
                    }}
                  >
                    {t('buttons.edit')}
                  </Button>
                </Stack>

                <Divider sx={{ mb: 2 }} />

                {/* Scheduling Information */}
                <Box sx={{ pl: 1 }}>
                  <EmaQuestionnaireNotificationScheduling
                    emaQuestionnaire={questionnaire}
                  />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 6,
            px: 3,
            textAlign: 'center',
            backgroundColor: 'grey.50',
            borderRadius: 2,
            border: '2px dashed',
            borderColor: 'grey.300',
          }}
        >
          <ScheduleIcon
            sx={{
              fontSize: 48,
              color: 'grey.400',
              mb: 2,
            }}
          />
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              fontStyle: 'italic',
              maxWidth: 400,
              lineHeight: 1.5,
            }}
          >
            {t('pages.studies.questionnaireScheduling.noQuestionnaires')}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
